import { useEffect, useState } from 'react';
import { superAdminApi } from '../../lib/superAdminApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Users, AlertOctagon, ShieldAlert, TrendingUp, Building2, Activity, ChevronRight, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const scoreColor = (s) => {
  if (s >= 80) return 'text-emerald-400';
  if (s >= 60) return 'text-amber-400';
  return 'text-red-400';
};
const churnBadge = (level) => {
  const map = {
    high: 'bg-red-500/20 text-red-300 border-red-500/40',
    medium: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
    low: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  };
  return map[level] || map.low;
};

export default function SuperAdminDashboard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    superAdminApi.dashboard().then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /></div>;
  if (!data) return null;

  const kpis = [
    { label: 'Tenants activos', value: data.totals.active_tenants, icon: Building2, gradient: 'from-cyan-500 to-blue-500' },
    { label: 'Utilizadores totais', value: data.totals.users, icon: Users, gradient: 'from-emerald-500 to-teal-500' },
    { label: 'Score médio', value: `${data.totals.avg_compliance_score}%`, icon: TrendingUp, gradient: 'from-amber-500 to-orange-500', colorClass: scoreColor(data.totals.avg_compliance_score) },
    { label: 'Incidentes críticos', value: data.totals.critical_incidents_open, icon: AlertOctagon, gradient: 'from-red-500 to-pink-500' },
    { label: 'Vulnerabilidades críticas', value: data.totals.critical_vulns_open, icon: ShieldAlert, gradient: 'from-orange-500 to-red-500' },
    { label: 'Churn risk HIGH', value: data.totals.high_churn_count, icon: Activity, gradient: 'from-red-600 to-red-400' },
  ];

  return (
    <div className="space-y-8" data-testid="sa-dashboard-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight" data-testid="sa-dashboard-title">Dashboard cross-tenant</h1>
        <p className="text-slate-400 mt-1">Métricas consolidadas de todos os tenants sob gestão</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <Card key={k.label} className="bg-slate-900/60 border-slate-800 hover:border-slate-700 transition-colors">
              <CardContent className="p-4">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${k.gradient} flex items-center justify-center mb-3`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className={`text-2xl font-bold ${k.colorClass || 'text-slate-100'}`}>{k.value}</div>
                <div className="text-xs text-slate-500 mt-1 uppercase tracking-wide">{k.label}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tenants list with score + churn */}
      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-slate-100">Tenants ({data.tenants.length})</CardTitle>
          <Button size="sm" onClick={() => nav('/super-admin/tenants')} className="bg-amber-500 hover:bg-amber-400 text-slate-950" data-testid="sa-manage-tenants-btn">
            Gerir tenants <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-slate-800">
            {data.tenants.map((t) => (
              <div key={t.tenant_id} className="p-4 hover:bg-slate-950/50 transition-colors" data-testid={`sa-tenant-row-${t.tenant.slug}`}>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-100">{t.tenant.name}</span>
                      <Badge variant="outline" className="border-slate-700 text-slate-400 text-xs">
                        {t.tenant.compliance_mode?.toUpperCase()}
                      </Badge>
                      {t.tenant.status !== 'active' && (
                        <Badge variant="outline" className="border-red-500/50 text-red-300 text-xs">{t.tenant.status}</Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      /{t.tenant.slug} · {t.users_count} users ({t.users_active_30d} active/30d) · {t.total_tasks} tarefas · {t.total_assets} assets
                    </div>
                    {t.churn_reasons?.length > 0 && (
                      <div className="text-xs text-amber-400/80 mt-1">⚠ {t.churn_reasons.join(' · ')}</div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-bold ${scoreColor(t.compliance_score)}`}>{t.compliance_score}%</div>
                    <div className="text-xs text-slate-500 uppercase tracking-wide">score</div>
                  </div>
                  <Badge className={`${churnBadge(t.churn_risk_level)} border`}>
                    churn: {t.churn_risk_level} ({t.churn_risk_score})
                  </Badge>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-slate-700 text-slate-300 hover:bg-slate-800"
                    onClick={() => nav(`/super-admin/tenants/${t.tenant.tenant_id}`)}
                    data-testid={`sa-open-tenant-${t.tenant.slug}`}
                  >
                    Abrir <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
